import { api } from '@goaltree/backend/convex/_generated/api'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useConvexAuth, useMutation, useQuery } from 'convex/react'
import { Leaf, Loader2 } from 'lucide-react'
import { motion } from 'motion/react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { GroveIcon } from '@/components/groves/grove-icon'
import { toastGroveError } from '@/components/groves/grove-utils'
import { StreakFlame } from '@/components/streak-icons'
import { clearReturnTo, stashReturnTo } from '@/lib/return-to'

export const Route = createFileRoute('/groves/join/$code')({
  head: () => ({
    meta: [{ title: 'Join a grove — Goaltree' }, { name: 'robots', content: 'noindex, nofollow' }],
  }),
  component: RouteComponent,
})

const primaryBtn =
  'flex h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-full bg-primary text-[15px] font-extrabold text-primary-foreground transition-colors hover:bg-primary/90 active:scale-[0.99] disabled:pointer-events-none disabled:opacity-50'

function RouteComponent() {
  const { t } = useTranslation()
  const { code } = Route.useParams()
  const navigate = useNavigate()
  const { isAuthenticated, isLoading } = useConvexAuth()
  const preview = useQuery(api.groves.previewInvite, { code })
  const join = useMutation(api.groves.join)
  const [joining, setJoining] = useState(false)

  useEffect(() => {
    if (isAuthenticated) clearReturnTo()
  }, [isAuthenticated])

  function signIn() {
    const returnTo = `/groves/join/${code}`
    stashReturnTo(returnTo)
    navigate({ to: '/auth/login', search: { returnTo } })
  }

  async function handleJoin() {
    setJoining(true)
    try {
      const groveId = await join({ code })
      navigate({ to: '/groves/$groveId', params: { groveId }, replace: true })
    } catch (err) {
      toastGroveError(err, t)
      setJoining(false)
    }
  }

  return (
    <div className="flex min-h-svh flex-col bg-background">
      <header className="shrink-0 border-b bg-background/80 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center gap-2 px-4 py-3">
          <img src="/favicon.svg?v=4" alt="Goaltree" className="h-7 w-7 select-none" />
          <span className="text-sm font-semibold tracking-tight">Goaltree</span>
        </div>
      </header>
      <main className="flex flex-1 items-center justify-center px-4 py-10">
        {preview === undefined || isLoading ? (
          <div className="w-full max-w-sm rounded-3xl border border-border/50 bg-card px-6 py-8">
            <div className="mx-auto size-20 animate-pulse rounded-3xl bg-muted/40" />
            <div className="mx-auto mt-4 h-5 w-2/3 animate-pulse rounded bg-muted/40" />
            <div className="mx-auto mt-2 h-3 w-1/2 animate-pulse rounded bg-muted/30" />
            <div className="mt-6 h-11 animate-pulse rounded-full bg-muted/40" />
          </div>
        ) : preview === null ? (
          <div className="flex flex-col items-center gap-3 text-center">
            <Leaf className="size-10 text-muted-foreground" strokeWidth={1.75} />
            <p className="text-lg font-bold text-foreground">{t('groves.join.invalidTitle')}</p>
            <p className="max-w-xs text-sm text-muted-foreground">{t('groves.join.invalidDesc')}</p>
            <Link
              to="/"
              className="mt-2 text-sm font-semibold text-primary-text underline-offset-4 hover:underline"
            >
              {t('share.goToGoaltree')}
            </Link>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-sm rounded-3xl border border-border/50 bg-card px-6 py-8 text-center"
          >
            <GroveIcon icon={preview.icon} size="lg" className="mx-auto" />
            <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('groves.join.invitedBy', { name: preview.ownerName })}
            </p>
            <h1 className="mt-1 text-xl font-extrabold tracking-tight text-foreground">
              {preview.name}
            </h1>
            {preview.description && (
              <p className="mt-1.5 text-sm text-muted-foreground">{preview.description}</p>
            )}
            <div className="mt-3 flex items-center justify-center gap-3 text-xs text-muted-foreground">
              <span>
                {t('groves.membersOf', { count: preview.memberCount, max: preview.maxMembers })}
              </span>
              {preview.groveStreak > 0 && (
                <span className="font-bold text-foreground">
                  <StreakFlame /> {preview.groveStreak}
                </span>
              )}
            </div>
            <p className="mt-4 text-sm text-muted-foreground">{t('groves.join.pitch')}</p>
            <div className="mt-6">
              {preview.alreadyMember ? (
                <button
                  type="button"
                  className={primaryBtn}
                  onClick={() =>
                    navigate({ to: '/groves/$groveId', params: { groveId: preview.groveId } })
                  }
                >
                  {t('groves.join.open')}
                </button>
              ) : preview.full ? (
                <p className="rounded-xl bg-muted/50 px-3 py-2.5 text-sm font-medium text-muted-foreground">
                  {t('groves.errors.full')}
                </p>
              ) : isAuthenticated ? (
                <button
                  type="button"
                  className={primaryBtn}
                  disabled={joining}
                  onClick={handleJoin}
                >
                  {joining && <Loader2 className="size-4 animate-spin" />}
                  {t('groves.join.submit')}
                </button>
              ) : (
                <button type="button" className={primaryBtn} onClick={signIn}>
                  {t('groves.join.signIn')}
                </button>
              )}
            </div>
          </motion.div>
        )}
      </main>
    </div>
  )
}
